import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import API_BASE from '../apiBase';
import { getStoredUser, setStoredUser, clearStoredUser } from '../utils/userStorage';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer as MuiDrawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { SidebarContext } from './SidebarContext';
import PageHeader from './PageHeader';
import { NotificationsProvider } from '../notifications/NotificationsProvider';

// Icons
import {
  Menu as MenuIcon,
  KeyboardDoubleArrowLeft as KeyboardDoubleArrowLeftIcon,
  DashboardCustomizeOutlined,
  FolderOutlined,
  RateReviewOutlined,
  AnalyticsOutlined,
  BugReportOutlined,
  SettingsOutlined,
  Person as PersonIcon,
  CardMembershipOutlined,
} from '@mui/icons-material';
import { BarChart3, CreditCard, FolderOpen, GitPullRequest, LayoutDashboard, PanelLeft, Settings, Target } from 'lucide-react';

const drawerWidth = 240; // Slightly reduced for cleaner look

// Base menu items (Azure Boards Analysis will be conditionally added)
const baseMenuItems = [
  { text: 'Dashboard', icon: <LayoutDashboard size={20}/>, path: '/app/dashboard' },
  { text: 'Projects', icon: <FolderOpen size={20}/>, path: '/app/projects' },
  { text: 'PR Reviews', icon: <GitPullRequest size={20}/>, path: '/app/pull-requests' },
  { text: 'PR Analysis', icon: <BarChart3 size={20}/>, path: '/app/pr-analysis' },
  { text: 'JIRA Analysis', icon: <Target size={20}/>, path: '/app/jira-analysis' },
  { text: 'Settings', icon: <Settings size={20}/>, path: '/app/settings' },
  { text: 'Manage Subscription', icon: <CreditCard size={20}/>, path: '/app/manage-subscription' },
];

// Page configuration for headers
const pageConfig = {
  '/app/dashboard': {
    title: 'Executive Dashboard',
    subtitle: 'Real-time insights for strategic decision making',
    icon: <DashboardCustomizeOutlined sx={{ fontSize: 16, color: '#6b7280' }} />
  },
  '/app/projects': {
    title: 'Projects',
    subtitle: 'Manage and monitor your development projects',
    icon: <FolderOutlined sx={{ fontSize: 16, color: '#6b7280' }} />
  },
  '/app/pull-requests': {
    title: 'Pull Request Reviews',
    subtitle: 'Commercial-grade PR analysis and issue tracking',
    icon: <RateReviewOutlined sx={{ fontSize: 16, color: '#6b7280' }} />
  },
  '/app/pr-analysis': {
    title: 'PR Analysis',
    subtitle: 'Detailed analysis of pull request metrics',
    icon: <AnalyticsOutlined sx={{ fontSize: 16, color: '#6b7280' }} />
  },
  '/app/jira-analysis': {
    title: 'JIRA Analysis',
    subtitle: 'Comprehensive JIRA ticket analysis and insights',
    icon: <BugReportOutlined sx={{ fontSize: 16, color: '#6b7280' }} />
  },
  '/app/azure-boards-analysis': {
    title: 'Azure Boards Analysis',
    subtitle: 'Comprehensive Azure Boards work item analysis and insights',
    icon: <BugReportOutlined sx={{ fontSize: 16, color: '#6b7280' }} />
  },
  '/app/trello-analysis': {
    title: 'Trello Analysis',
    subtitle: 'Comprehensive Trello card analysis and insights',
    icon: <BugReportOutlined sx={{ fontSize: 16, color: '#6b7280' }} />
  },
  '/app/settings': {
    title: 'Settings',
    subtitle: 'Configure cycle time calculations for your development workflow',
    icon: <SettingsOutlined sx={{ fontSize: 16, color: '#6b7280' }} />
  },
  '/app/manage-subscription': {
    title: 'Manage Subscription',
    subtitle: 'Manage your subscription and view payment history',
    icon: <CardMembershipOutlined sx={{ fontSize: 16, color: '#6b7280' }} />
  },
  '/app/profile-settings': {
    title: 'Profile Settings',
    subtitle: 'Manage your personal profile and preferences',
    icon: <PersonIcon sx={{ fontSize: 16, color: '#6b7280' }} />
  }
};

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  borderRight: 'none',
  backgroundColor: '#0b1220', // Darker background to match image
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: 0,
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: 52, // Show only icons when collapsed
  borderRight: 'none',
  backgroundColor: '#0b1220',
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: 0,
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

// --- HEADER (AppBar) ---
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})(({ theme, open, isMobile }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)',
  backgroundColor: '#ffffff', // White background to match image
  color: '#1f1f1f', // Dark text
  minHeight: 64, // Slightly taller for better proportion
  borderRadius: 0,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid #e5e7eb',
  ...(isMobile ? {} : {
    marginLeft: open ? drawerWidth : 72,
    width: open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - 52px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

// --- SIDEBAR (Drawer) ---
const StyledDrawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    background: '#0b1220',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': {
        ...openedMixin(theme),
        background: '#0b1220',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
      },
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': {
        ...closedMixin(theme),
        background: '#0b1220',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderBottomLeftRadius: 0,
      },
    }),
  })
);


function Layout() {
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [hasCheckedProjects, setHasCheckedProjects] = useState(false);
  const [lastDowngradeCheck, setLastDowngradeCheck] = useState(0);
  const [hasAzureBoardsProjects, setHasAzureBoardsProjects] = useState(false);
  const [hasTrelloProjects, setHasTrelloProjects] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  // Build menu items dynamically based on whether Azure Boards or Trello projects exist
  const menuItems = useMemo(() => {
    const items = [...baseMenuItems];
    // Insert Azure Boards Analysis after JIRA Analysis if there are Azure Boards projects
    if (hasAzureBoardsProjects) {
      const jiraIndex = items.findIndex(item => item.text === 'JIRA Analysis');
      if (jiraIndex !== -1) {
        items.splice(jiraIndex + 1, 0, {
          text: 'Azure Boards Analysis',
          icon: <Target size={20}/>,
          path: '/app/azure-boards-analysis'
        });
      }
    }
    // Insert Trello Analysis after Azure Boards (or JIRA if no Azure Boards) if there are Trello projects
    if (hasTrelloProjects) {
      const azureBoardsIndex = items.findIndex(item => item.text === 'Azure Boards Analysis');
      const insertIndex = azureBoardsIndex !== -1 ? azureBoardsIndex + 1 : items.findIndex(item => item.text === 'JIRA Analysis') + 1;
      if (insertIndex > 0) {
        items.splice(insertIndex, 0, {
          text: 'Trello Analysis',
          icon: <Target size={20}/>,
          path: '/app/trello-analysis'
        });
      }
    }
    return items;
  }, [hasAzureBoardsProjects, hasTrelloProjects]);

  // Get current page configuration
  const getCurrentPageConfig = () => {
    // Find the best matching route
    const currentPath = location.pathname;

    // Check for exact match first
    if (pageConfig[currentPath]) {
      return pageConfig[currentPath];
    }

    // Check for partial matches (for nested routes)
    const matchingRoute = Object.keys(pageConfig).find(route =>
      currentPath.startsWith(route)
    );

    if (matchingRoute) {
      return pageConfig[matchingRoute];
    }

    // Default fallback
    return {
      title: 'Dashboard',
      subtitle: 'Welcome to your dashboard',
      icon: <DashboardCustomizeOutlined sx={{ fontSize: 16, color: '#6b7280' }} />
    };
  };

  // Check if user has valid payment or projects, redirect to plans if not
  const checkUserAccess = async () => {
    try {
      const { getIdTokenSafe } = await import('../firebase');
      const idToken = await getIdTokenSafe(false);
      if (!idToken) {
        // Try a forced refresh once before redirecting
        const refreshed = await getIdTokenSafe(true);
        if (!refreshed) {
          navigate('/login');
          return;
        }
      }

      // Ensure user exists and cached locally
      try {
        if (!getStoredUser()) {
          await axios.post(`${API_BASE}/api/users/bootstrap`);
          const { data: me } = await axios.get(`${API_BASE}/api/users/me`);
          if (me) {
            setStoredUser(me);
            setUser(me);
          }
        }
      } catch (error) {
        console.error('Failed to bootstrap user:', error);
      }

      // Check for plan changes (upgrade, downgrade, or recurring) and handle accordingly (throttled)
      try {
        const nowTs = Date.now();
        if (nowTs - lastDowngradeCheck > 60000) {
          await axios.post(`${API_BASE}/api/payments/check-plan-changes`);
          setLastDowngradeCheck(nowTs);
          console.log('✅ Plan change check completed');
        }
      } catch (planChangeError) {
        console.log('⚠️ Plan change check failed:', planChangeError);
        // Don't fail navigation if plan change check fails
      }

      // Check if user has a valid payment or projects for all app routes
      if (location.pathname.startsWith('/app/')) {
        // New: prioritize latest-subscription to allow immediate access after successful payment
        try {
          const latestResp = await axios.get(`${API_BASE}/api/payments/latest-subscription`);
          if (latestResp.data && latestResp.data.has_subscription === true) {
            const isExpired = latestResp.data.is_expired === true;
            const sub = latestResp.data.subscription;
            if (!isExpired && sub && (sub.status === 'success' || sub.status === 'trial')) {
              // Latest subscription indicates active window; allow access
              return;
            }
          }
        } catch (e) {
          // Ignore and fall through to existing checks
        }

        // Fallback: check current-subscription (success or trial)
        try {
          const paymentResponse = await axios.get(`${API_BASE}/api/payments/current-subscription`);
          if (paymentResponse.data.subscription) {
            // User has a valid payment, allow access
            return;
          }
        } catch (paymentError) {
          // No valid payment found, proceed to check projects
        }

        // If no valid payment, check if user has projects
        const projectsResponse = await axios.get(`${API_BASE}/api/projects`);

        if (projectsResponse.data.length === 0) {
          // User has no projects and no valid payment, redirect to plans page
          navigate('/plans');
        } else {
          // Check if any project has Azure Boards enabled
          const hasAzureBoards = projectsResponse.data.some(project => project.azure_boards_enabled === true);
          setHasAzureBoardsProjects(hasAzureBoards);
          // Check if any project has Trello enabled
          const hasTrello = projectsResponse.data.some(project => project.trello_enabled === true);
          setHasTrelloProjects(hasTrello);
        }
      }
    } catch (error) {
      console.error('Error checking user access:', error);
      // If there's an error, don't redirect - let the user stay on the current page
    }
  };

  // Get user data from localStorage and listen for changes
  useEffect(() => {
    const loadUserData = () => {
      const stored = getStoredUser();
      if (stored) {
        setUser(stored);
      }
    };

    // Load initial data
    loadUserData();

    // Listen for storage events (when localStorage is updated from other tabs/windows)
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        loadUserData();
      }
    };

    // Listen for custom event (when localStorage is updated from same tab)
    const handleUserUpdate = () => {
      loadUserData();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userUpdated', handleUserUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  // Sign out and redirect whenever Firebase auth state becomes null (token revoked / session expired)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (!firebaseUser) {
        clearStoredUser();
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Check for Azure Boards and Trello projects when user is loaded
  useEffect(() => {
    const checkProjectIntegrations = async () => {
      if (!user) return;
      
      try {
        const projectsResponse = await axios.get(`${API_BASE}/api/projects`);
        const hasAzureBoards = projectsResponse.data.some(project => project.azure_boards_enabled === true);
        setHasAzureBoardsProjects(hasAzureBoards);
        const hasTrello = projectsResponse.data.some(project => project.trello_enabled === true);
        setHasTrelloProjects(hasTrello);
      } catch (error) {
        console.error('Error checking project integrations:', error);
      }
    };

    checkProjectIntegrations();
  }, [user]);

  // Check user access when component mounts and user is loaded
  useEffect(() => {
    if (user && location.pathname.startsWith('/app')) {
      checkUserAccess();
    }
  }, [user, location.pathname]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleLogout = async () => {
    try {
      const { auth } = await import('../firebase');
      const { signOut } = await import('firebase/auth');
      await signOut(auth);
    } catch {}
    clearStoredUser();
    navigate('/login');
  };

  const handleProfileSettings = () => {
    navigate('/app/profile-settings');
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawerContent = (
    <>
      <DrawerHeader sx={{ minHeight: { xs: 64, md: 80 }, px: { xs: 2, md: 3 }, justifyContent: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 }, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 } }}>
            <Box
              component="img"
              src="/assets/logo.png"
              alt="Sniffr.ai Logo"
              sx={{ height: { xs: 28, md: 32 }, width: { xs: 28, md: 32 } }}
              borderRadius={5}
            />
            <Typography
              variant="h6"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                fontSize: { xs: '16px', md: '18px' },
                 fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                opacity: open ? 1 : 0,
                transition: 'opacity 0.2s ease'
              }}
            >
              Sniffr.ai
            </Typography>
          </Box>
          {!isMobile && (
            <Box sx={{ ml: 'auto' }}>
              <Tooltip title={open ? "Collapse sidebar" : "Expand sidebar"} placement="right">
                <IconButton
                  onClick={open ? handleDrawerClose : handleDrawerOpen}
                  size="small"
                  sx={{
                    color: '#9ca3af',
                    '&:hover': {
                      color: '#ffffff',
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    },
                    transition: 'all 0.2s ease',
                  }}
                  aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
                >
                  {open ? (
                    <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <MenuIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {isMobile && (
            <Box sx={{ ml: 'auto' }}>
              <Tooltip title="Close" placement="right">
                <IconButton
                  onClick={handleDrawerToggle}
                  size="small"
                  sx={{
                    color: '#9ca3af',
                    '&:hover': {
                      color: '#ffffff',
                      backgroundColor: 'rgba(255,255,255,0.1)'
                    },
                    transition: 'all 0.2s ease',
                  }}
                  aria-label="Close sidebar"
                >
                  <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </DrawerHeader>
      <List sx={{ px: 2, pt: 1,display: 'flex', flexDirection: 'column', gap: 1 }}>
        {menuItems.map((item) => (
          <ListItemButton
            key={item.text}
            onClick={() => handleNavigate(item.path)}
            selected={location.pathname.startsWith(item.path)}
            sx={{
              mb: 0.5,
              borderRadius: '8px',
              py: 1,
              px: 1.5,
              minHeight: 40,
              color: '#737373',
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              fontWeight: 500,
              transition: 'all 0.2s ease',
              '&.Mui-selected': {
                backgroundColor: '#fff',
                color: '#1f1f1f',
                fontWeight: 600,
                '& .MuiListItemIcon-root': {
                  color: '#1f1f1f',
                },
                '&:hover': {
                  backgroundColor: '#fff',
                  color: '#1f1f1f',
                },
              },
              '&:hover': {
                backgroundColor: '#fff',
                  color: '#1f1f1f',
              },
            }}
          >
            <Tooltip title={item.text} placement="right" disableHoverListener={isMobile || open}>
              <ListItemIcon sx={{
                color: 'inherit',
                minWidth: '32px',
              }}>
                {item.icon}
              </ListItemIcon>
            </Tooltip>
            <ListItemText
              primary={item.text}
              sx={{
                opacity: (isMobile || open) ? 1 : 0,
                '& .MuiListItemText-primary': {
                  fontSize: '14px',
                  fontWeight: 'inherit',
                   fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"'
                }
              }}
            />
          </ListItemButton>
        ))}
      </List>


    </>
  );

  return (
    <NotificationsProvider>
    <Box sx={{ display: 'flex',  fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"' }}>
      <CssBaseline />
      <StyledAppBar position="fixed" open={!isMobile && open} isMobile={isMobile}>
        <Toolbar sx={{ width:  { xs: '8%', sm: '6%', md: '4%' }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: { xs: 56, md: 64 }, px: { xs: 2, md: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="center">
            {(isMobile || !open) && (
              <Tooltip title="Expand sidebar" placement="right">
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={isMobile ? handleDrawerToggle : handleDrawerOpen}
                  edge="start"
                  sx={{
                    // marginRight: { xs: 1, md: 1 },
                    marginLeft: '-0.5rem',
                    color: '#1f1f1f',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.04)'
                    }
                  }}
                >
                  <PanelLeft size={18}/>
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
        </Toolbar>
        <Box sx={{ width: { xs: '92%', sm: '94%', md: '96%' }}}>
          <PageHeader
            {...getCurrentPageConfig()}
            user={user}
            onProfileSettings={handleProfileSettings}
            onLogout={handleLogout}
          />
        </Box>
      </StyledAppBar>
      {isMobile ? (
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: drawerWidth,
              background: '#0b1220',
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              borderBottomLeftRadius: 0,
            }
          }}
        >
          {drawerContent}
        </MuiDrawer>
      ) : (
        <StyledDrawer variant="permanent" open={open}>
          {drawerContent}
        </StyledDrawer>
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0, // Remove padding since pages handle their own padding
          backgroundColor: '#f8fafc', // Updated to match professional styling
          minHeight: '100vh',
          fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: 0,
          width: isMobile ? '100%' : (open ? `calc(100% - ${drawerWidth}px)` : `calc(100% - 72px)`),
          ...(isMobile ? {} : {
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          })
        }}
      >
        <DrawerHeader />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <SidebarContext.Provider value={{ open, drawerWidth }}>
            <Outlet />
          </SidebarContext.Provider>
        </motion.div>
      </Box>
    </Box>
    </NotificationsProvider>
  );
}

export default Layout;